import type { ReactNode } from 'react';
import type { Key, Selection } from 'react-aria-components';
import { msg } from '@gonfalon/messages/msg';
import {
  Group,
  Header,
  IconButton,
  Input,
  Menu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Popover,
  type PopoverProps,
  ProgressBar,
  SearchField,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import styles from './MultiSelectMenu.module.css';

export type MultiSelectMenuItem = {
  id: string;
  label?: string;
};

export type MultiSelectMenuSearch = {
  'aria-label': string;
  placeholder?: string;
  value?: string;
  onChange: (value: string) => void;
};

export type MultiSelectMenuProps = {
  trigger: ReactNode;
  'aria-label': string;
  items: MultiSelectMenuItem[];
  selectedKeys: Iterable<Key>;
  onSelectionChange: (keys: Selection) => void;
  emptyState: ReactNode;
  disabledKeys?: Iterable<Key>;
  disallowEmptySelection?: boolean;
  escapeKeyBehavior?: 'clearSelection' | 'none';
  header?: ReactNode;
  search?: MultiSelectMenuSearch;
  isLoading?: boolean;
  loadingLabel?: string;
  placement?: PopoverProps['placement'];
  onOpenChange?: (isOpen: boolean) => void;
};

export function MultiSelectMenu({
  trigger,
  'aria-label': ariaLabel,
  items,
  selectedKeys,
  onSelectionChange,
  emptyState,
  disabledKeys,
  disallowEmptySelection,
  escapeKeyBehavior,
  header,
  search,
  isLoading = false,
  loadingLabel,
  placement = 'bottom left',
  onOpenChange,
}: MultiSelectMenuProps) {
  const menuItems = items.map((item) => (
    <MenuItem id={item.id} key={item.id} className={styles.menuItem} aria-label={item.label}>
      {item.label}
    </MenuItem>
  ));

  return (
    <MenuTrigger onOpenChange={onOpenChange}>
      {trigger}
      <Popover placement={placement} className={styles.popover}>
        {search && (
          <div className={styles.searchFieldContainer}>
            <SearchField aria-label={search['aria-label']} value={search.value} onChange={search.onChange}>
              <Group>
                <Icon name="search" size="small" />
                <Input placeholder={search.placeholder} />
                <IconButton
                  aria-label={msg('ui.multiSelect.clearLabel')}
                  icon="cancel-circle-outline"
                  size="small"
                  variant="minimal"
                />
              </Group>
            </SearchField>
          </div>
        )}
        <Menu
          onSelectionChange={onSelectionChange}
          selectedKeys={selectedKeys}
          disabledKeys={disabledKeys}
          selectionMode="multiple"
          escapeKeyBehavior={escapeKeyBehavior}
          className={styles.menu}
          aria-label={ariaLabel}
          disallowEmptySelection={disallowEmptySelection}
          renderEmptyState={() => <div className={styles.menuEmptyState}>{emptyState}</div>}
        >
          {header && items.length > 0 ? (
            <MenuSection>
              <Header>{header}</Header>
              {menuItems}
            </MenuSection>
          ) : (
            menuItems
          )}
        </Menu>
        {isLoading && (
          <div className={styles.loading}>
            <ProgressBar isIndeterminate aria-label={loadingLabel} className={styles.progress} />
          </div>
        )}
      </Popover>
    </MenuTrigger>
  );
}
